import { useMemo } from 'react';
import { useAccountAutoPricingSettings } from 'src/navigations/Routes/Settings/Views/AccountAutoPricingSettings/AccountAutoPricingSettings.hooks';
import { CompListingQuantity, Listing } from 'src/WebApiController';

export const quantitiesOptions = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10+',
].reduce(
  (r, c) => {
    r[c] = c;
    return r;
  },
  {} as Record<string, string>
);

export const useSelectedQuantityFilters = (
  compListingQuantityFilters?: CompListingQuantity | null,
  compListingQuantityScoreAdjustmentOverrideJson?: string | null
) => {
  const parsedQuantityOverridesJson = useMemo(() => {
    return compListingQuantityScoreAdjustmentOverrideJson != null
      ? JSON.parse(compListingQuantityScoreAdjustmentOverrideJson)
      : null;
  }, [compListingQuantityScoreAdjustmentOverrideJson]);

  const derivedCompListingQuantityFilters =
    useMemo((): CompListingQuantity | null => {
      if (compListingQuantityFilters != null) {
        return compListingQuantityFilters;
      }

      if (parsedQuantityOverridesJson == null) {
        return null;
      }

      const { exact, gte } =
        parsedQuantityOverridesJson?.quantitiesFilter ?? {};
      return {
        exactQuantities: exact ?? [],
        greaterThanOrEqualToQuantity: gte,
      };
    }, [compListingQuantityFilters, parsedQuantityOverridesJson]);

  const selectedQuantitiesValues = useMemo(() => {
    if (!derivedCompListingQuantityFilters) {
      return [];
    }

    const { exactQuantities: exact, greaterThanOrEqualToQuantity: gte } =
      derivedCompListingQuantityFilters;

    const eligibleQuantities = Object.values(quantitiesOptions)
      .filter((value) => !isNaN(parseInt(value)))
      .map((value) => parseInt(value));
    const selectedQuantitiesInts = eligibleQuantities.filter((quantity) => {
      if (((exact as number[]) ?? []).includes(quantity)) {
        return true;
      }
      if (gte != null && (gte as number) <= quantity) {
        return true;
      }
      return false;
    });
    return Object.values(quantitiesOptions).filter((value) =>
      selectedQuantitiesInts.includes(parseInt(value))
    );
  }, [derivedCompListingQuantityFilters]);

  return selectedQuantitiesValues;
};

export const useDefaultQuantityFilters = (listing?: Listing) => {
  const { accountAutoPricingSettings } = useAccountAutoPricingSettings();

  const quantityFilters = useMemo(() => {
    if (
      !listing ||
      !accountAutoPricingSettings?.defaultCompListingQuantityFilters
    ) {
      return null;
    }
    const { greaterThanOrEqualToQuantityFilter, exactMatchQuantityFilters } =
      accountAutoPricingSettings.defaultCompListingQuantityFilters;

    const { maxDispQty, manMaxDispQty, availQty } = listing;
    let listingQuantity = Math.min(availQty, maxDispQty);
    if (manMaxDispQty != null) {
      listingQuantity = Math.min(listingQuantity, manMaxDispQty);
    }

    if (
      greaterThanOrEqualToQuantityFilter?.posListingQuantity &&
      listingQuantity >= greaterThanOrEqualToQuantityFilter.posListingQuantity
    ) {
      return greaterThanOrEqualToQuantityFilter.compQuantityFilters;
    }

    return (
      exactMatchQuantityFilters?.find(
        (filters) => filters.posListingQuantity === listingQuantity
      )?.compQuantityFilters ?? null
    );
  }, [accountAutoPricingSettings?.defaultCompListingQuantityFilters, listing]);

  return quantityFilters;
};
